import type { Partner } from "@expert/shared-types";

import { Button, Center, Overlay, Text } from "@mantine/core";
import cx from "clsx";
import { type FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { CheckmarkBounce } from "@/common-ui/animations/checkmark-bounce";
import sharedStyles from "@/common-ui/sharedStyles.module.css";

import type { TranscriptContent } from "../api/evaluations/types";

import { getChecklistCategoryCount, sortBehaviorsByChecklist } from "../utils";
import EvaluationCallout from "./EvaluationCallout";
import classes from "./EvaluationPage.module.css";
import EvaluationTranscript from "./EvaluationTranscript";
import SalesBehaviorsChecklist from "./SalesBehaviorsChecklist";

export interface TrainingEvaluationPageProps {
    onCompleteEvaluation: () => void;
    evaluationResults: TranscriptContent;
    partner: Partner;
}

export const TrainingEvaluationPage: FC<TrainingEvaluationPageProps> = ({
    onCompleteEvaluation,
    evaluationResults,
    partner,
}) => {
    const transcriptContainerRef = useRef<HTMLDivElement>(null);
    const [activeBehaviorIndex, setActiveBehaviorIndex] = useState(0);
    const [scrollOffset, setScrollOffset] = useState(0);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [completeEvaluation, setCompleteEvaluation] = useState(false);
    // Determine which set of behaviors to use
    const behaviors = useMemo(() => {
        const orderedBehaviors = sortBehaviorsByChecklist(evaluationResults.EvaluationDetails.Body, partner);
        return orderedBehaviors;
    }, [evaluationResults, partner]);
    const activeBehaviorLine = useMemo(() => {
        if (behaviors[activeBehaviorIndex]?.behaviorLine?.length) {
            return behaviors[activeBehaviorIndex].behaviorLine[0];
        } else {
            return undefined;
        }
    }, [behaviors, activeBehaviorIndex]);
    const pagination = useMemo(() => {
        if (behaviors[activeBehaviorIndex]?.behavior) {
            const { currentIndex, total } = getChecklistCategoryCount({
                category: behaviors[activeBehaviorIndex]?.category,
                behavior: behaviors[activeBehaviorIndex].behavior,
                presentBehaviors: behaviors,
                partner,
            });
            return { currentIndex, total };
        } else {
            return { currentIndex: 0, total: 0 };
        }
    }, [behaviors, activeBehaviorIndex, partner]);

    useEffect(() => {
        if (completeEvaluation) {
            onCompleteEvaluation();
        }
    }, [completeEvaluation, onCompleteEvaluation]);

    useLayoutEffect(() => {
        if (transcriptContainerRef?.current) {
            const offset = transcriptContainerRef.current.scrollHeight / 2 - 100;
            setScrollOffset(offset);
        }
    }, []);

    return (
        <div className={classes.background}>
            <div className={classes.container}>
                {overlayVisible && (
                    <Overlay data-testid="return-home-screen" color="var(--exwo-color-app-bg)" backgroundOpacity={0.85}>
                        <Center
                            style={{ flexDirection: "column", height: "100%", gap: "1.5rem" }}
                            className={sharedStyles.animateScaleIn}
                        >
                            <CheckmarkBounce />
                            <Text fw="700" ta="center" size="xl">
                                Thank you for reviewing!
                            </Text>
                            <Button
                                onClick={() => {
                                    setCompleteEvaluation(true);
                                }}
                            >
                                Return Home
                            </Button>
                        </Center>
                    </Overlay>
                )}
                <div className={classes.evaluationGrid}>
                    <div className={cx(classes.evaluationGridColumn, classes.sidebar)}>
                        <SalesBehaviorsChecklist
                            behaviors={behaviors}
                            activeBehaviorCategory={behaviors[activeBehaviorIndex]?.category}
                            activeBehaviorIndex={activeBehaviorIndex}
                            onClickBehavior={setActiveBehaviorIndex}
                            partner={partner}
                        />
                    </div>
                    <div className={cx(classes.evaluationGridColumn, classes.content)} ref={transcriptContainerRef}>
                        <EvaluationTranscript
                            activeBehaviorLine={activeBehaviorLine}
                            behaviors={behaviors}
                            evaluationResults={evaluationResults}
                            onClickHighlight={(transcriptBehavior) => {
                                if (transcriptBehavior) {
                                    const newBehaviorIndex = behaviors.findIndex(
                                        ({ behavior }) => behavior === transcriptBehavior,
                                    );
                                    const index = newBehaviorIndex >= 0 ? newBehaviorIndex : 0;
                                    setActiveBehaviorIndex(index);
                                } else {
                                    setActiveBehaviorIndex(0);
                                }
                            }}
                            scrollOffset={scrollOffset}
                        />
                    </div>
                    <div className={cx(classes.evaluationGridColumn, classes.calloutCard)}>
                        <EvaluationCallout
                            key={behaviors[activeBehaviorIndex]?.behavior}
                            activeBehavior={behaviors[activeBehaviorIndex]}
                            onClickOk={() => {
                                const nextBehavior = activeBehaviorIndex + 1;

                                if (nextBehavior >= behaviors.length) {
                                    setOverlayVisible(true);
                                } else {
                                    setActiveBehaviorIndex(nextBehavior);
                                }
                            }}
                            pagination={
                                behaviors.length > 0 ? (
                                    <Text size="xxs" style={{ textWrap: "nowrap" }}>
                                        {pagination.currentIndex} of {pagination.total}
                                    </Text>
                                ) : null
                            }
                            scrollOffset={scrollOffset}
                            transcriptLength={evaluationResults?.Transcriptions.length ?? 0}
                            trainingSessionId={evaluationResults?.SessionID}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrainingEvaluationPage;
